import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import { useAuth } from '../../contexts/AuthContext';
import { cacheCurrentListId } from '../../utils/listCache';

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Get email and save action info from location state or URL params
  const email = location.state?.email || new URLSearchParams(location.search).get('email') || '';
  const isFromSaveAction = location.state?.fromSave === true;
  const isFromNewList = location.state?.fromNewList === true;
  const isFromSignUp = location.state?.fromSignUp === true;
  const returnPath = location.state?.returnPath;
  const isFromPublicList = location.state?.isFromPublicList === true;
  const fromFreeTrialIntent = location.state?.fromFreeTrialIntent === true;

  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...verificationCode];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newCode[i] = pastedData[i];
    }
    
    setVerificationCode(newCode);
    
    // Focus the next empty input or the last input
    const nextEmptyIndex = newCode.findIndex((digit, index) => !digit && index < 6);
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const code = verificationCode.join('');
    if (code.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch('/api/v1/auth/verify-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          verificationCode: code,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Always check for session data or create default list after email verification
        const sessionData = {
          tickerList: sessionStorage.getItem('stocktable-ticker-list') ? JSON.parse(sessionStorage.getItem('stocktable-ticker-list')!) : null,
          listName: sessionStorage.getItem('stocktable-list-name'),
          hiddenStatistics: sessionStorage.getItem('stocktable-hidden-statistics') ? JSON.parse(sessionStorage.getItem('stocktable-hidden-statistics')!) : null
        };
        
        console.log('🔍 Retrieved session data from sessionStorage:', sessionData);
        
        // Pass locationState so saveSessionDataToAccount knows the context
        const locationState = {
          fromSave: isFromSaveAction,
          fromNewList: isFromNewList,
          fromSignUp: isFromSignUp,
          returnPath: returnPath,
          isFromPublicList: isFromPublicList,
          fromFreeTrialIntent: fromFreeTrialIntent
        };
        
        // If user came from a save action, new list creation, or sign up button, save their session data
        if (isFromSaveAction || isFromNewList || isFromSignUp) {
          if (sessionData.tickerList) {
            await saveSessionDataToAccount(data.accessToken, sessionData, locationState);
          } else {
            console.log('⚠️ No ticker list found to save');
            // No session data to save - create default "Main List" if user has no lists
            const { createDefaultMainListIfNeeded } = await import('../../utils/sessionDataUtils');
            await createDefaultMainListIfNeeded(data.accessToken);
          }
        } else {
          // User came directly to sign up (not from save action) - create default list if no session data
          if (sessionData.tickerList && sessionData.tickerList.length > 0) {
            await saveSessionDataToAccount(data.accessToken, sessionData, locationState);
          } else {
            console.log('⚠️ No session data - creating default "Main List" for new user');
            const { createDefaultMainListIfNeeded } = await import('../../utils/sessionDataUtils');
            await createDefaultMainListIfNeeded(data.accessToken);
          }
        }
        
        // Login the user using the auth context
        login(data.accessToken, data.user, data.refreshToken);
        
        // Redirect to the appropriate page
        // Pass fromGetStarted so list pages show "Start here!" tooltip for new users with empty list
        const showGetStartedTooltip = { state: { fromGetStarted: true } };
        // If user came from "Start Free Trial Now" and just verified (new account), go to order-summary
        if (fromFreeTrialIntent) {
          navigate('/order-summary', { state: { freeTrialEligible: true } });
        } else if (isFromPublicList) {
          navigate('/daily', showGetStartedTooltip);
        } else if (isFromSaveAction && returnPath) {
          navigate(returnPath, showGetStartedTooltip);
        } else if (isFromNewList && returnPath) {
          navigate(returnPath, showGetStartedTooltip);
        } else if (isFromSignUp && returnPath) {
          navigate(returnPath, showGetStartedTooltip);
        } else {
          navigate('/daily', showGetStartedTooltip);
        }
      } else {
        setError(data.message || 'Verification failed. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');

    try {
      const response = await fetch('/api/v1/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        // Clear the current code
        setVerificationCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        // You could show a success message here
      } else {
        setError(data.message || 'Failed to resend code. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsResending(false);
    }
  };

  // Function to save session data to user account
  const saveSessionDataToAccount = async (accessToken: string, sessionData?: any, locationState?: any) => {
    try {
      console.log('🔍 saveSessionDataToAccount called with flags:', {
        isFromSaveAction,
        isFromNewList,
        isFromSignUp,
        returnPath
      });
      
      if (isFromNewList) {
        // For new list creation, save the user's current Main List with all their data
        console.log('Creating new list for user after signup with their current data');
        
        // Use provided session data or get from sessionStorage
        const tickerList = sessionData?.tickerList || sessionStorage.getItem('stocktable-ticker-list');
        const listName = sessionData?.listName || sessionStorage.getItem('stocktable-list-name');
        const hiddenStatistics = sessionData?.hiddenStatistics || sessionStorage.getItem('stocktable-hidden-statistics');
        
        if (!tickerList || !listName) {
          console.log('No session data to save for new list creation');
          return;
        }

        const tickers = Array.isArray(tickerList) ? tickerList : JSON.parse(tickerList);
        // Handle both old flat array structure and new categorized structure
        let hiddenStats;
        if (Array.isArray(hiddenStatistics)) {
          // Old flat array structure
          hiddenStats = hiddenStatistics;
        } else if (typeof hiddenStatistics === 'string') {
          // JSON string that needs parsing
          hiddenStats = JSON.parse(hiddenStatistics);
        } else if (typeof hiddenStatistics === 'object' && hiddenStatistics !== null) {
          // New categorized object structure
          hiddenStats = hiddenStatistics;
        } else {
          // Fallback to empty object
          hiddenStats = {};
        }
        
        console.log('Saving Main List data for new user:', {
          listName,
          tickers,
          tickerCount: tickers.length,
          tickerOrder: tickers.map((ticker: string, index: number) => ({ ticker, order: index + 1 })),
          hiddenStatistics: hiddenStats
        });
        
        // Create the list with the user's current list name
        const createListResponse = await fetch('/api/v1/user-ticker-list/create_list', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            list_name: listName
          }),
        });

        if (!createListResponse.ok) {
          throw new Error('Failed to create new list');
        }

        const createListData = await createListResponse.json();
        const listId = createListData.data.id;
        
        // Cache the new list ID so the user gets redirected to the correct list
        cacheCurrentListId(listId);
        console.log('📦 Cached new list ID:', listId);

        // Add each ticker to the list with proper order
        for (let i = 0; i < tickers.length; i++) {
          const ticker = tickers[i];
          const orderNum = i + 1; // 1-based ordering
          
          try {
            const response = await fetch(`/api/v1/user-ticker-list/${listId}/tickers`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                ticker_symbol: ticker,
                order_num: orderNum
              }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              console.error(`Failed to add ticker ${ticker} at position ${orderNum}:`, errorData);
            } else {
              console.log(`Successfully added ticker ${ticker} to list at position ${orderNum}`);
            }
          } catch (error) {
            console.error(`Failed to add ticker ${ticker} at position ${orderNum}:`, error);
            // Continue with other tickers even if one fails
          }
        }

        // Save list preferences if they exist
        const hasPreferences = hiddenStats && (
          (Array.isArray(hiddenStats) && hiddenStats.length > 0) ||
          (typeof hiddenStats === 'object' && (
            (hiddenStats.daily && hiddenStats.daily.length > 0) ||
            (hiddenStats.technicals && hiddenStats.technicals.length > 0) ||
            (hiddenStats.fundamentals && hiddenStats.fundamentals.length > 0) ||
            (hiddenStats.analystViews && hiddenStats.analystViews.length > 0)
          ))
        );
        
        if (hasPreferences) {
          try {
            console.log('Saving list preferences for new user:', hiddenStats);
            
            // Use the new categorized structure - indicators are already categorized by page type
            const preferencesData: any = {
              userTickerListId: listId
            };
            
            // Set the appropriate fields based on the categorized structure
            if (hiddenStats.daily && hiddenStats.daily.length > 0) {
              preferencesData.dailyIndicatorsToHide = hiddenStats.daily;
            }
            if (hiddenStats.technicals && hiddenStats.technicals.length > 0) {
              preferencesData.technicalIndicatorsToHide = hiddenStats.technicals;
            }
            if (hiddenStats.fundamentals && hiddenStats.fundamentals.length > 0) {
              preferencesData.fundamentalIndicatorsToHide = hiddenStats.fundamentals;
            }
            if (hiddenStats.analystViews && hiddenStats.analystViews.length > 0) {
              preferencesData.analystIndicatorsToHide = hiddenStats.analystViews;
            }
            
            const preferencesResponse = await fetch('/api/v1/user-ticker-list/preferences', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify(preferencesData),
            });

            if (!preferencesResponse.ok) {
              console.error('Failed to save list preferences:', await preferencesResponse.text());
            } else {
              console.log('Successfully saved list preferences for new user');
            }
          } catch (error) {
            console.error('Failed to save list preferences:', error);
          }
        }

        // Save individual ticker preferences
        await saveTickerPreferences(accessToken, tickers);

        // Clear session data after successful save
        sessionStorage.removeItem('stocktable-ticker-list');
        sessionStorage.removeItem('stocktable-list-name');
        sessionStorage.removeItem('stocktable-hidden-statistics');
        
        // Clear individual ticker preferences from sessionStorage
        clearTickerPreferencesFromSession(tickers);
        
        console.log('Main List created successfully for new user:', {
          listId,
          listName,
          tickersProcessed: tickers.length,
          preferencesSaved: hasPreferences
        });
        
        // Now create a new empty list for the user to work with
        console.log('Creating new empty list for user to work with...');
        
        const newListResponse = await fetch('/api/v1/user-ticker-list/create_list', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            list_name: 'New List'
          }),
        });

        if (!newListResponse.ok) {
          console.error('Failed to create new empty list');
        } else {
          const newListData = await newListResponse.json();
          const newListId = newListData.data.id;
          
          // Cache the new list ID so the user gets redirected to the new empty list
          cacheCurrentListId(newListId);
          console.log('📦 Cached new empty list ID:', newListId);
        }
        
        return;
      }
      
      // For save action or sign up, save the current session data
      console.log('🔍 Executing save action/sign up logic');
      const tickerList = sessionData?.tickerList || sessionStorage.getItem('stocktable-ticker-list');
      const listName = sessionData?.listName || sessionStorage.getItem('stocktable-list-name');
      const hiddenStatistics = sessionData?.hiddenStatistics || sessionStorage.getItem('stocktable-hidden-statistics');
      
      console.log('🔍 Retrieved session data:', {
        tickerList,
        listName,
        hiddenStatistics,
        hasTickerList: !!tickerList,
        hasListName: !!listName,
        hasHiddenStats: !!hiddenStatistics
      });
      
      if (!tickerList || !listName) {
        console.log('No session data to save');
        return;
      }

      const tickers = Array.isArray(tickerList) ? tickerList : JSON.parse(tickerList);
      // Handle both old flat array structure and new categorized structure
      let hiddenStats;
      if (Array.isArray(hiddenStatistics)) {
        // Old flat array structure
        hiddenStats = hiddenStatistics;
      } else if (typeof hiddenStatistics === 'string') {
        // JSON string that needs parsing
        hiddenStats = JSON.parse(hiddenStatistics);
      } else if (typeof hiddenStatistics === 'object' && hiddenStatistics !== null) {
        // New categorized object structure
        hiddenStats = hiddenStatistics;
      } else {
        // Fallback to empty object
        hiddenStats = {};
      }
      
      console.log('Saving session data to account:', {
        listName,
        tickers,
        tickerCount: tickers.length,
        tickerOrder: tickers.map((ticker: string, index: number) => ({ ticker, order: index + 1 })),
        hiddenStatistics: hiddenStats
      });
      
      // Create the list
      const createListResponse = await fetch('/api/v1/user-ticker-list/create_list', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          list_name: listName
        }),
      });

      if (!createListResponse.ok) {
        throw new Error('Failed to create list');
      }

      const createListData = await createListResponse.json();
      const listId = createListData.data.id;

      // Add each ticker to the list with proper order
      for (let i = 0; i < tickers.length; i++) {
        const ticker = tickers[i];
        const orderNum = i + 1; // 1-based ordering
        
        try {
          const response = await fetch(`/api/v1/user-ticker-list/${listId}/tickers`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              ticker_symbol: ticker,
              order_num: orderNum
            }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error(`Failed to add ticker ${ticker} at position ${orderNum}:`, errorData);
          } else {
            console.log(`Successfully added ticker ${ticker} to list at position ${orderNum}`);
          }
        } catch (error) {
          console.error(`Failed to add ticker ${ticker} at position ${orderNum}:`, error);
          // Continue with other tickers even if one fails
        }
      }

      // Save list preferences if they exist
      const hasPreferences = hiddenStats && (
        (Array.isArray(hiddenStats) && hiddenStats.length > 0) ||
        (typeof hiddenStats === 'object' && (
          (hiddenStats.daily && hiddenStats.daily.length > 0) ||
          (hiddenStats.technicals && hiddenStats.technicals.length > 0) ||
          (hiddenStats.fundamentals && hiddenStats.fundamentals.length > 0) ||
          (hiddenStats.analystViews && hiddenStats.analystViews.length > 0)
        ))
      );
      
      if (hasPreferences) {
        try {
          console.log('Saving list preferences:', hiddenStats);
          
          // Use the new categorized structure - indicators are already categorized by page type
          const preferencesData: any = {
            userTickerListId: listId
          };
          
          // Set the appropriate fields based on the categorized structure
          if (hiddenStats.daily && hiddenStats.daily.length > 0) {
            preferencesData.dailyIndicatorsToHide = hiddenStats.daily;
          }
          if (hiddenStats.technicals && hiddenStats.technicals.length > 0) {
            preferencesData.technicalIndicatorsToHide = hiddenStats.technicals;
          }
          if (hiddenStats.fundamentals && hiddenStats.fundamentals.length > 0) {
            preferencesData.fundamentalIndicatorsToHide = hiddenStats.fundamentals;
          }
          if (hiddenStats.analystViews && hiddenStats.analystViews.length > 0) {
            preferencesData.analystIndicatorsToHide = hiddenStats.analystViews;
          }
          
          const preferencesResponse = await fetch('/api/v1/user-ticker-list/preferences', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify(preferencesData),
          });

          if (!preferencesResponse.ok) {
            console.error('Failed to save list preferences:', await preferencesResponse.text());
          } else {
            console.log('Successfully saved list preferences');
          }
        } catch (error) {
          console.error('Failed to save list preferences:', error);
        }
      }

      // Save individual ticker preferences
      await saveTickerPreferences(accessToken, tickers);

      // Clear session data after successful save
      sessionStorage.removeItem('stocktable-ticker-list');
      sessionStorage.removeItem('stocktable-list-name');
      sessionStorage.removeItem('stocktable-hidden-statistics');
      
      // Clear individual ticker preferences from sessionStorage
      clearTickerPreferencesFromSession(tickers);
      
      console.log('Session data saved to account successfully:', {
        listId,
        listName,
        tickersProcessed: tickers.length,
        preferencesSaved: hasPreferences
      });
    } catch (error) {
      console.error('Failed to save session data to account:', error);
      // Don't throw error to avoid breaking the verification flow
    }
  };

  // Helper function to save individual ticker preferences
  const saveTickerPreferences = async (accessToken: string, tickers: string[]) => {
    for (const ticker of tickers) {
      try {
        // Get ticker basics ID first
        const syncResponse = await fetch('/api/v1/ticker/check-sync-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            ticker_symbols: [ticker]
          }),
        });

        if (!syncResponse.ok) {
          console.error(`Failed to get sync status for ${ticker}`);
          continue;
        }

        const syncData = await syncResponse.json();
        const tickerInfo = syncData.data.tickers.find((t: any) => t.ticker_symbol === ticker);
        
        if (!tickerInfo) {
          console.error(`Ticker ${ticker} not found in sync response`);
          continue;
        }

        const tickerBasicsId = tickerInfo.ticker_basics_id;
        
        // Check for ticker-specific preferences in sessionStorage
        const technicalHiddenStats = sessionStorage.getItem(`stocktable-ticker-technicals-hidden-statistics-${ticker}`);
        const technicalHiddenColumns = sessionStorage.getItem(`stocktable-ticker-technicals-hidden-columns-${ticker}`);
        const fundamentalHiddenStats = sessionStorage.getItem(`stocktable-ticker-fundamentals-hidden-statistics-${ticker}`);
        const analystHiddenStats = sessionStorage.getItem(`stocktable-ticker-analyst-views-hidden-statistics-${ticker}`);

        // Save technical preferences if they exist
        if (technicalHiddenStats || technicalHiddenColumns) {
          const technicalStats = technicalHiddenStats ? JSON.parse(technicalHiddenStats) : [];
          const technicalColumns = technicalHiddenColumns ? JSON.parse(technicalHiddenColumns) : [];
          
          if (technicalStats.length > 0 || technicalColumns.length > 0) {
            const technicalPreferences = {
              tickerBasicsId,
              technicalIndicatorsToHide: technicalStats,
              technicalDaysAgoToHide: technicalColumns
            };

            const response = await fetch('/api/v1/user-ticker/preferences', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify(technicalPreferences),
            });

            if (!response.ok) {
              console.error(`Failed to save technical preferences for ${ticker}:`, await response.text());
            } else {
              console.log(`Successfully saved technical preferences for ${ticker}`);
            }
          }
        }

        // Save fundamental preferences if they exist
        if (fundamentalHiddenStats) {
          const fundamentalStats = JSON.parse(fundamentalHiddenStats);
          
          if (fundamentalStats.length > 0) {
            const fundamentalPreferences = {
              tickerBasicsId,
              fundamentalIndicatorsToHide: fundamentalStats
            };

            const response = await fetch('/api/v1/user-ticker/preferences', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify(fundamentalPreferences),
            });

            if (!response.ok) {
              console.error(`Failed to save fundamental preferences for ${ticker}:`, await response.text());
            } else {
              console.log(`Successfully saved fundamental preferences for ${ticker}`);
            }
          }
        }

        // Save analyst preferences if they exist
        if (analystHiddenStats) {
          const analystStats = JSON.parse(analystHiddenStats);
          
          if (analystStats.length > 0) {
            const analystPreferences = {
              tickerBasicsId,
              analystIndicatorsToHide: analystStats
            };

            const response = await fetch('/api/v1/user-ticker/preferences', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify(analystPreferences),
            });

            if (!response.ok) {
              console.error(`Failed to save analyst preferences for ${ticker}:`, await response.text());
            } else {
              console.log(`Successfully saved analyst preferences for ${ticker}`);
            }
          }
        }

      } catch (error) {
        console.error(`Failed to save preferences for ticker ${ticker}:`, error);
        // Continue with other tickers even if one fails
      }
    }
  };

  // Helper function to clear ticker preferences from sessionStorage
  const clearTickerPreferencesFromSession = (tickers: string[]) => {
    for (const ticker of tickers) {
      sessionStorage.removeItem(`stocktable-ticker-technicals-hidden-statistics-${ticker}`);
      sessionStorage.removeItem(`stocktable-ticker-technicals-hidden-columns-${ticker}`);
      sessionStorage.removeItem(`stocktable-ticker-fundamentals-hidden-statistics-${ticker}`);
      sessionStorage.removeItem(`stocktable-ticker-analyst-views-hidden-statistics-${ticker}`);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background">
      <Header showSignUp={false} showLogIn={false} />
      
      <main className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-lg space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-text-light-primary dark:text-text-primary">
              Enter verification code
            </h2>
            <p className="mt-2 text-sm text-text-light-secondary dark:text-text-secondary">
              A 6-digit code has been sent to <span className="font-medium">{email}</span>
            </p>
          </div>
          
          <div className="bg-surface-light dark:bg-background-card p-4 sm:p-8 rounded-lg shadow-lg border border-border-light dark:border-border-color">
            <form onSubmit={handleSubmit}>
              <div className="flex justify-center gap-1 sm:gap-2 mb-6">
                {verificationCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="w-9 h-12 sm:w-12 sm:h-14 md:w-14 md:h-16 text-center text-xl sm:text-2xl font-semibold bg-white dark:bg-background border border-border-light dark:border-border-color rounded-md text-text-light-primary dark:text-text-primary focus:outline-none focus:border-primary dark:focus:border-highlight-gold focus:ring-1 focus:ring-primary dark:focus:ring-highlight-gold flex-shrink-0"
                    autoComplete="off"
                  />
                ))}
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}
              
              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={isLoading || verificationCode.join('').length !== 6}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-semibold text-gray-900 bg-primary hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-light dark:focus:ring-offset-background-card focus:ring-primary dark:focus:ring-highlight-gold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Verifying...' : 'Verify'}
                </button>
                
                <p className="text-center text-sm text-text-light-secondary dark:text-text-secondary">
                  Didn't receive the code?{' '}
                  <button
                    type="button"
                    onClick={handleResendCode}
                    disabled={isResending}
                    className="font-medium text-primary dark:text-highlight-gold hover:text-opacity-80 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isResending ? 'Sending...' : 'Resend code'}
                  </button>
                </p>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmailVerification;
